import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';

// Import components
import Navigation from './components/Navigation/Navigation';
import Hero from './components/Hero/Hero';
import Services from './components/Services/Services';
import About from './components/About/About';
import Contact from './components/Contact/Contact';
import Footer from './components/Footer/Footer';
import RemoteAccess from './components/RemoteAccess/RemoteAccess';
import RemoteMonitoring from './components/RemoteMonitoring/RemoteMonitoring';
import CustomSoftware from './components/CustomSoftware/CustomSoftware';
import Resources from './components/Resources/Resources';
import ArticleView from './components/Resources/ArticleView';
import Support from './components/Support/Support';
import CaseStudies from './components/CaseStudies/CaseStudies';
import Branding from './components/Branding/Branding';
import NotFound from './components/NotFound/NotFound';

// Import styles
import './styles/main.scss';
import './App.css';

/**
 * ScrollToTop - Component that scrolls to top on route change or reload
 */
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

/**
 * ScrollToSection - Helper component to handle scrolling after navigation
 */
const ScrollToSection = () => {
  const { state } = useLocation();
  
  useEffect(() => {
    // Check if we have a scrollTo parameter in the router state
    if (state && state.scrollTo) {
      const section = document.getElementById(state.scrollTo);
      
      if (section) {
        // Add a small delay to ensure the page has fully rendered
        setTimeout(() => {
          // Use scrollIntoView with smooth behavior
          section.scrollIntoView({ 
            behavior: 'smooth',
            // The scroll-margin-top CSS property handles the offset,
            // so we don't need to manually calculate it here
          });
        }, 300); // Increased delay to ensure page is fully loaded
      }
    }
  }, [state]);
  
  return null;
};

/**
 * App Component - Main entry point for the MET Connect® website
 * 
 * Handles routing and layout for the entire application
 * 
 * @returns {JSX.Element} The App component
 */
function App() {
  return (
    <Router>
      <div className="App">
        <ScrollToTop />
        <Navigation />
        <ScrollToSection />
        
        <Routes>
          {/* Home Page Route */}
          <Route path="/" element={
            <>
              <Hero />
              <Services id="services" />
              <CaseStudies id="case-studies" />
              <About />
              <Contact />
            </>
          } />
          
          {/* Remote Access Page Route */}
          <Route path="/remote-access" element={
            <RemoteAccess />
          } />
          
          {/* Remote Monitoring Page Route */}
          <Route path="/remote-monitoring" element={
            <RemoteMonitoring />
          } />
          
          {/* Custom Software Page Route */}
          <Route path="/custom-software" element={
            <CustomSoftware />
          } />
          
          {/* Resources Page Route */}
          <Route path="/resources" element={
            <Resources />
          } />
          
          {/* Knowledge Articles Routes */}
          <Route path="/knowledge/:articleId" element={
            <ArticleView />
          } />
          
          {/* Support Page Route */}
          <Route path="/support" element={
            <Support />
          } />
          
          {/* Branding Page Route - not included in navigation */}
          <Route path="/branding" element={
            <Branding />
          } />
          
          {/* 404 Not Found Route */}
          <Route path="*" element={
            <NotFound />
          } />
        </Routes>
        
        <Footer />
      </div>
    </Router>
  );
}

export default App;
